// src/map/StreamingPath.tsx
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

export type StreamingPathHandle = {
  reset(): void;
  append(points: Array<[number, number]>): void;
  clear(): void;
  preflyToBounds(args: {
    bounds: string;
    maxZoom?: number;
    padding?: [number, number];
    fly?: boolean;
  }): Promise<boolean>;
};

const StreamingPath = forwardRef<StreamingPathHandle, {
  color?: string; weight?: number; opacity?: number; pane?: string
}>(({ color = red[500], weight = 4, opacity = 1, pane = 'paths' }, ref) => {
  const map = useMap();
  const polyRef = useRef<L.Polyline | null>(null);
  const latlngsRef = useRef<L.LatLng[]>([]);

  useEffect(() => {
    const poly = L.polyline([], { color, weight, opacity, pane });
    poly.addTo(map);
    polyRef.current = poly;

    return () => {
      poly.remove();
      polyRef.current = null;
    };
  }, [map, color, weight, opacity, pane]);

  useImperativeHandle(ref, () => ({
    reset() {
      latlngsRef.current = [];
      polyRef.current?.setLatLngs([]);
    },
    append(points) {
      for (const [lat, lng] of points) {
        latlngsRef.current.push(L.latLng(lat, lng));
      }
      polyRef.current?.setLatLngs(latlngsRef.current);
    },
    clear() {
      latlngsRef.current = [];
      polyRef.current?.setLatLngs([]);
    },
    async preflyToBounds({
      bounds, maxZoom = 16, padding = [20, 20], fly = true
    }) {
      if (!bounds.isValid()) return false;

      return new Promise<boolean>((resolve) => {
        const onEnd = () => { map.off("moveend", onEnd); resolve(true); };
        map.on("moveend", onEnd);
        if (fly) map.flyToBounds(bounds, { maxZoom, padding });
        else map.fitBounds(bounds, { maxZoom, padding });
        // Safety: if bounds don’t cause a move, resolve right away
        if (!map._animating && !map._panAnim) {
          map.off("moveend", onEnd);
          resolve(true);
        }
      });
    }
  }), [map]);

  return null; // imperative-only layer
});

export default StreamingPath;
